<template>
  <component
    v-if="iconComponent"
    :is="iconComponent"
    :size="sizeVal"
    :stroke-width="strokeWidth"
    :color="colorVal"
    class="base-icon"
  />
</template>

<script>
import {
  Activity, AlertCircle, AlertTriangle, ArrowDown, ArrowUp, BarChart3, Bot, Brain, Briefcase,
  Building2, Check, ChevronDown, ChevronLeft, ChevronRight, ChevronUp,
  ClipboardCheck, ClipboardList, Clock, ExternalLink, Eye, EyeOff, FileCheck,
  Files, FileText, GraduationCap, Heart, History, Inbox, Info, LayoutDashboard,
  LineChart, LogIn, LogOut, Lock, Mail, Menu, Network, Paperclip, Pencil, Plus, RefreshCw,
  Rocket, Search, ShieldAlert, Sparkles, Stethoscope, Trash2, Users, X,
  Download, Link, QrCode, Settings, Upload, Flag, MessageSquarePlus,
} from 'lucide-vue';

// Mapa MDI → componente Lucide (renderiza ícones com o nome original)
const MDI_TO_LUCIDE = {
  'mdi-account-group-outline':            Users,
  'mdi-alert-circle-outline':             AlertCircle,
  'mdi-alert-outline':                    AlertTriangle,
  'mdi-arrow-down':                       ArrowDown,
  'mdi-arrow-up':                         ArrowUp,
  'mdi-brain':                            Brain,
  'mdi-briefcase-outline':                Briefcase,
  'mdi-chart-box-outline':                BarChart3,
  'mdi-chart-line':                       LineChart,
  'mdi-check':                            Check,
  'mdi-chevron-down':                     ChevronDown,
  'mdi-chevron-left':                     ChevronLeft,
  'mdi-chevron-right':                    ChevronRight,
  'mdi-chevron-up':                       ChevronUp,
  'mdi-clipboard-list-outline':           ClipboardList,
  'mdi-clipboard-text-outline':           ClipboardCheck,
  'mdi-clock-outline':                    Clock,
  'mdi-close':                            X,
  'mdi-comment-plus-outline':             MessageSquarePlus,
  'mdi-flag-outline':                     Flag,
  'mdi-cloud-upload-outline':             Upload,
  'mdi-cog-outline':                      Settings,
  'mdi-domain':                           Building2,
  'mdi-download':                         Download,
  'mdi-email-outline':                    Mail,
  'mdi-form-select':                      FileText,
  'mdi-inbox-multiple-outline':           Inbox,
  'mdi-link-variant':                     Link,
  'mdi-office-building-outline':          Building2,
  'mdi-qrcode':                           QrCode,
  'mdi-eye-off-outline':                  EyeOff,
  'mdi-eye-outline':                      Eye,
  'mdi-file-check-outline':               FileCheck,
  'mdi-file-document-multiple-outline':   Files,
  'mdi-file-pdf-box':                     FileText,
  'mdi-heart-outline':                    Heart,
  'mdi-history':                          History,
  'mdi-inbox-outline':                    Inbox,
  'mdi-information-outline':              Info,
  'mdi-information-variant':              Info,
  'mdi-lock-outline':                     Lock,
  'mdi-login-variant':                    LogIn,
  'mdi-logout':                           LogOut,
  'mdi-magnify':                          Search,
  'mdi-menu':                             Menu,
  'mdi-open-in-new':                      ExternalLink,
  'mdi-paperclip':                        Paperclip,
  'mdi-pencil-outline':                   Pencil,
  'mdi-pencil':                           Pencil,
  'mdi-plus':                             Plus,
  'mdi-pulse':                            Activity,
  'mdi-refresh':                          RefreshCw,
  'mdi-robot-outline':                    Bot,
  'mdi-rocket-launch-outline':            Rocket,
  'mdi-school-outline':                   GraduationCap,
  'mdi-sitemap-outline':                  Network,
  'mdi-shield-alert-outline':             ShieldAlert,
  'mdi-sparkles':                         Sparkles,
  'mdi-stethoscope':                      Stethoscope,
  'mdi-trash-can-outline':                Trash2,
  'mdi-view-dashboard-outline':           LayoutDashboard,
};

// Mapeia cores nomeadas (compat com Vuetify) → hex
const COLOR_ALIASES = {
  'primary':           '#20B1AC',
  'secondary':         '#0F4C4A',
  'error':             '#C0292B',
  'red':               '#C0292B',
  'red lighten-1':     '#E04E50',
  'warning':           '#B07419',
  'success':           '#1F9D55',
  'info':              '#1976D2',
  'grey':              '#98A2AC',
  'grey lighten-1':    '#B6BEC6',
  'grey lighten-2':    '#D1D7DD',
  'grey darken-1':     '#6B7785',
  'grey darken-2':     '#4A5762',
  'purple':            '#9C27B0',
  'white':             '#FFFFFF',
};

export default {
  name: 'BaseIcon',
  props: {
    // Pode receber via prop name ou via default slot (compat com v-icon)
    name:        { type: String, default: '' },
    size:        { type: [String, Number], default: 18 },
    strokeWidth: { type: [String, Number], default: 1.75 },
    color:       { type: String, default: 'currentColor' },
  },
  computed: {
    resolvedName() {
      if (this.name) return this.name;
      // Slot fallback — pega o texto do default slot
      const slot = this.$slots.default;
      if (slot && slot[0] && typeof slot[0].text === 'string') {
        return slot[0].text.trim();
      }
      return '';
    },
    iconComponent() {
      const n = this.resolvedName;
      // Aceita "mdi-foo" ou "foo" (sem prefixo)
      if (MDI_TO_LUCIDE[n]) return MDI_TO_LUCIDE[n];
      const withPrefix = `mdi-${n}`;
      if (MDI_TO_LUCIDE[withPrefix]) return MDI_TO_LUCIDE[withPrefix];
      // eslint-disable-next-line no-console
      if (n) console.warn(`[BaseIcon] ícone não mapeado: "${n}"`);
      return null;
    },
    sizeVal() {
      // Vuetify aceita "16" string, número ou tamanhos nomeados (small/large)
      const presets = { 'x-small': 12, 'small': 14, 'default': 18, 'large': 24, 'x-large': 32 };
      if (presets[this.size]) return presets[this.size];
      return Number(this.size) || this.size;
    },
    colorVal() {
      return COLOR_ALIASES[this.color] || this.color || 'currentColor';
    },
  },
};
</script>

<style scoped>
.base-icon { display: inline-block; vertical-align: -3px; flex-shrink: 0; }
</style>
